// Vendors
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { isEmpty, isEqual } from 'lodash';

import { getApplianceOptions } from './helpers';
import { snakeCaseToTitleCase } from '../../../../../helpers.js';

const constructionTypeMapping = {
  New: 'NEWCONSTRUCTION',
  Remodel: 'RETROFIT'
};

const Epat = ({
  projectData: {
    pid,
    city,
    state,
    zipcode,
    construction_type,
    epat_form_data,
    ...projectData
  },
  handleChangeView,
  updateSpecificProjectDataProperty
}) => {
  const savedOptions = epat_form_data ? JSON.parse(epat_form_data) : {};
  const initialEpatData = {
    baseline: [
      { name: 'SO2 (lbs)', total: projectData.epat_emissions_baseline_so2 },
      { name: 'NOx (lbs)', total: projectData.epat_emissions_baseline_nox },
      {
        name: 'CO2 (1000 lbs)',
        total: projectData.epat_emissions_baseline_co2
      },
      { name: 'CH4 (lbs)', total: projectData.epat_emissions_baseline_ch4 },
      { name: 'N2O (lbs)', total: projectData.epat_emissions_baseline_n2o },
      {
        name: 'CO2e (1000 lbs)',
        total: projectData.epat_emissions_baseline_co2e
      }
    ],
    alternative: [
      { name: 'SO2 (lbs)', total: projectData.epat_emissions_alternative_so2 },
      { name: 'NOx (lbs)', total: projectData.epat_emissions_alternative_nox },
      {
        name: 'CO2 (1000 lbs)',
        total: projectData.epat_emissions_alternative_co2
      },
      { name: 'CH4 (lbs)', total: projectData.epat_emissions_alternative_ch4 },
      { name: 'N2O (lbs)', total: projectData.epat_emissions_alternative_n2o },
      {
        name: 'CO2e (1000 lbs)',
        total: projectData.epat_emissions_alternative_co2e
      }
    ]
  };
  const initialFormData = epat_form_data
    ? { ...{ city, state, zipcode }, ...savedOptions }
    : {
        city,
        state,
        zipcode,
        equipmentCostBasis: constructionTypeMapping[construction_type],
        buildingSelection: '',
        size: 0,
        numberOfPeople: 0,
        baseline: [],
        alternative: []
      };

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState(initialFormData);
  const [epatData, setEpatData] = useState(initialEpatData);
  const [isFormChanged, setIsFormChanged] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    // if form has been submitted, clear success message
    if (successMessage) {
      setSuccessMessage('');
    }

    const isChanged = !isEqual(formData, initialFormData);
    setIsFormChanged(isChanged);
  }, [formData]);

  const applianceOptions = getApplianceOptions(
    projectData,
    wlPipConfig.equipmentOptions
  );

  const showUnitsOption = [
    'Apt Building 2-4 units',
    'Apt Building 5+ units'
  ].includes(formData.house_type);

  const handleInputChange = ({ target: { value, name } }) => {
    setFormData({
      ...formData,
      [name]: value
    });

    const filteredErrors = Object.entries(errors).filter(
      ([key]) => key !== name
    );
    const updatedErrors = Object.fromEntries(filteredErrors);

    setErrors({
      ...updatedErrors
    });
  };

  const handleEquipmentSelect = ({ target: { value, name } }) => {
    const baselineOrAlternative = name.split('_')[0];
    const appliance = name.split('_')[1];
    const applianceMatch = applianceOptions[appliance];
    const id = applianceMatch.options.find((option) => option.id == value)?.id;
    const equipmentId = applianceMatch.equipmentId;

    const newBaselineOrAlternative = [
      ...formData[baselineOrAlternative].filter(
        (obj) => obj.equipmentId !== equipmentId
      ),
      ...(typeof id !== 'undefined' ? [{ id, equipmentId }] : []) // ugly but it works
    ];

    setFormData({
      ...formData,
      [baselineOrAlternative]: newBaselineOrAlternative
    });

    const errorToFilter = errors[appliance];
    const newErrors = { ...errors };
    delete newErrors[appliance];

    const filteredError =
      errorToFilter &&
      Object.entries(errorToFilter).filter(
        ([key]) => key !== baselineOrAlternative
      );
    const updatedError = filteredError && Object.fromEntries(filteredError);

    if (!isEmpty(updatedError)) {
      newErrors[appliance] = updatedError;
    }

    setErrors({ ...newErrors });
  };

  const calculateEpat = () => {
    setLoading(true);
    setErrors({});
    setError('');

    // set up form data
    const epatData = new FormData();
    epatData.append('action', 'wl_pip_calculate_epat');
    epatData.append('pid', pid);
    epatData.append('data', JSON.stringify(formData));
    epatData.append(
      'applianceOptions',
      JSON.stringify(
        Object.keys(applianceOptions).map((key) => ({
          appliance: key,
          equipmentId: applianceOptions[key].equipmentId
        }))
      )
    );

    axios
      .post(window.wlPipConfig.ajaxUrl, epatData)
      .then(({ data: { success, data } }) => {
        setLoading(false);
        setIsFormChanged(false);
        const { epatData } = data;
        setEpatData(epatData);

        // update project data state
        Object.entries(epatData).forEach(([emissionType, emissionsData]) => {
          emissionsData.forEach(({ id, total }) => {
            const key = `epat_emissions_${emissionType}_${id}`;
            updateSpecificProjectDataProperty(key, total);
          });
        });

        setSuccessMessage('The EPAT calculation has been successfully saved');
      })
      .catch((error) => {
        setLoading(false);

        if (error.response) {
          setError(error.response.data.data.message);
          if (error.response.data.data.errors) {
            setErrors(error.response.data.data.errors);
          }
        } else if (error.request) {
          setError('There was an error with the request');
        } else {
          setError('There was an error');
        }
      });
  };

  return (
    <div id="upload-epat">
      <div className="grid u--font-size-lg">
        <div className="grid__col-xs-12">
          <div className="form__field form__field--required">
            <label className="form__label u--font-weight-bold">
              House Type
            </label>
            <div
              className={`form__select form__select--lg form__input--no-shadow ${
                errors.buildingSelection && ' form__field--error'
              }`}
            >
              <select
                name="buildingSelection"
                className="form__input form__input--lg"
                value={formData.buildingSelection}
                onChange={(event) => handleInputChange(event)}
              >
                <option value="">-- Please select house type --</option>
                {wlPipConfig.buildingTypes &&
                  wlPipConfig.buildingTypes.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.buildingType}
                    </option>
                  ))}
              </select>
            </div>
            <small className="form__help u--margin-top-10 u--color-red">
              {Boolean(errors.buildingSelection) && errors.buildingSelection}
            </small>
          </div>
        </div>
        <div className="grid__col-xs-12 grid__col-md-6">
          <div className="form__field form__field--required">
            <label className="form__label u--font-weight-bold">
              Number of Occupants {showUnitsOption && ' (Per unit)'}
            </label>
            <input
              type="number"
              name="numberOfPeople"
              className="form__input"
              value={formData.numberOfPeople}
              onChange={(event) => handleInputChange(event)}
            />
          </div>
        </div>
        <div className="grid__col-xs-12 grid__col-md-6">
          <div
            className={`form__field form__field--required ${
              errors.size && ' form__field--error'
            }`}
          >
            <label className="form__label u--font-weight-bold">
              Building Size (SQ FT {showUnitsOption && ' Per unit'})
            </label>
            <input
              type="number"
              name="size"
              className="form__input"
              value={formData.size}
              onChange={(event) => handleInputChange(event)}
            />
          </div>
          <small className="form__help u--margin-top-10 u--color-red">
            {Boolean(errors.size) && errors.size}
          </small>
        </div>
        {Object.keys(applianceOptions).map((dropdown) => (
          <div
            key={dropdown}
            className="grid__col-xs-12 u--display-flex u--justify-content-space-between u--flex-wrap-wrap-md-max"
          >
            {Array.from(['baseline', 'alternative']).map((section, i) => (
              <div
                className={`grid__col-xs-12 grid__col-md-6 u--padding-${
                  i % 2 != 0 ? 'right' : 'left'
                }-0-md-min u--padding-0-sm-max`}
                key={section}
              >
                <div
                  className={`form__field form__field--required ${
                    Boolean(errors[dropdown]) &&
                    Boolean(errors[dropdown][section]) &&
                    ' form__field--error'
                  }`}
                >
                  <label className="form__label u--font-weight-bold">
                    {snakeCaseToTitleCase(section) +
                      ' ' +
                      snakeCaseToTitleCase(dropdown)}
                  </label>
                  {Boolean(errors[dropdown]) && (
                    <small className="form__help u--margin-top-10 u--color-red">
                      {errors[dropdown][section]}
                    </small>
                  )}
                  <div className="form__select form__select--lg form__input--no-shadow">
                    <select
                      name={`${section}_${dropdown}`}
                      className="form__input form__input--lg"
                      value={
                        (Boolean(formData[section]?.length) &&
                          formData[section].find(
                            (equipment) =>
                              equipment.equipmentId ===
                              applianceOptions[dropdown]?.equipmentId
                          )?.id) ||
                        ''
                      }
                      onChange={(event) => handleEquipmentSelect(event)}
                    >
                      <option value="">-- Please Choose an Option --</option>
                      {section === 'baseline'
                        ? // Render all options for the baseline section
                          applianceOptions[dropdown].options.map((option) => (
                            <option key={option.id} value={option.id}>
                              {option.efficiencyName}
                            </option>
                          ))
                        : // For alternative, only render options that include 'propane'
                          applianceOptions[dropdown].options
                            .filter((option) =>
                              option.efficiencyName
                                .toLowerCase()
                                .includes('propane')
                            )
                            .map((option) => (
                              <option key={option.id} value={option.id}>
                                {option.efficiencyName}
                              </option>
                            ))}
                    </select>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ))}

        {Boolean(epatData && Object.keys(epatData).length) && (
          <div className="grid__col-xs-12">
            <h3>EPAT Data</h3>
            <table className="epat__table">
              <thead>
                <tr>
                  <th></th>
                  {epatData.baseline.map((item) => (
                    <th key={item.name}>{item.name}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Baseline: </td>
                  {epatData.baseline.map((item, key) => (
                    <td key={key}>{item.total}</td>
                  ))}
                </tr>
                <tr>
                  <td>Alternative: </td>
                  {epatData.alternative.map((item, key) => (
                    <td key={key}>{item.total}</td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {error && (
          <div className="notice notice--error u--margin-left-20" role="alert">
            {error}
          </div>
        )}

        {successMessage && (
          <div
            className="notice notice--success u--margin-left-20"
            role="alert"
          >
            {successMessage}
          </div>
        )}

        <div
          className="grid__col-xs-12 u--display-flex u--flex-wrap-wrap"
          style={{ gap: '2rem' }}
        >
          <button
            id="pip-add__save-epat"
            className={`btn btn--gold btn--lg module-hover-root ${
              loading || !isFormChanged ? 'btn--disabled' : ''
            } ${loading ? 'btn--loading' : ''}`}
            type="button"
            onClick={() => calculateEpat()}
            disabled={loading || !isFormChanged}
          >
            <span className="btn__content">
              <span className="btn__text">Calculate EPAT</span>
            </span>
          </button>
          <span
            className="btn btn--white btn--lg module-hover-root"
            onClick={() => handleChangeView('project-form')}
          >
            <span className="btn__content">
              <span className="btn__text">Back to Project Overview</span>
            </span>
          </span>
        </div>
      </div>
    </div>
  );
};
export default Epat;
